/**
 * Initializes the scroll-to-anchor functionality.
 *
 * This function checks if an element with the `data-scroll` attribute is present.
 * If found, it lazily imports the `scrollToAnchor` module and initializes it.
 * Errors during the import or initialization process are caught, logged, and rethrown.
 *
 * @throws Will throw an error if the scroll-to-anchor module fails to load or initialize.
 */
export const initializeScrollToAnchor = async (): Promise<void> => {
  const scrollElement = document.querySelector<HTMLElement>('[data-scroll]');

  if (!scrollElement) {
    return; // Exit if the scroll element is not found
  }

  try {
    const { scrollToAnchor } = await import(
      '@ms/shared/utils/scroll-to-anchor'
    );

    scrollToAnchor.init();
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error';

    // eslint-disable-next-line no-console
    console.error(
      `🚨 Failed to load or initialize scroll-to-anchor: ${errorMessage}`,
    );

    throw new Error(`Failed to initialize scroll-to-anchor: ${errorMessage}`);
  }
};
